import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { PenLine } from 'lucide-react';

// Score selector with toggle-off (3-8)
export const ScoreSelector = ({ value, onChange, disabled }) => (
    <div className="flex gap-0.5" data-testid="poe-score-selector">
        {[3, 4, 5, 6, 7, 8].map(s => (
            <button
                key={s}
                type="button"
                disabled={disabled}
                onClick={() => onChange(value === s ? null : s)}
                data-testid={`poe-score-${s}`}
                className={`w-7 h-7 rounded text-xs font-semibold border transition-all ${
                    value === s
                        ? 'bg-ica-navy text-white border-ica-navy'
                        : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-300 border-gray-300 dark:border-slate-600 hover:border-ica-navy disabled:opacity-40 disabled:cursor-not-allowed'
                }`}
            >
                {s}
            </button>
        ))}
    </div>
);

export const SignatureField = ({ value, label, onClick, disabled }) => (
    <div className="space-y-1">
        <Label className="text-xs text-gray-500 dark:text-slate-400">{label}</Label>
        {value ? (
            <img src={value} alt={label} className="h-12 object-contain border border-gray-200 dark:border-slate-600 rounded bg-white cursor-pointer" onClick={() => !disabled && onClick()} />
        ) : (
            <Button variant="outline" size="sm" onClick={onClick} disabled={disabled} className="h-9">
                <PenLine className="w-3.5 h-3.5 mr-1" /> Sign
            </Button>
        )}
    </div>
);

const Card = ({ children, className = '' }) => (
    <div className={`bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-5 ${className}`}>{children}</div>
);

// Sort items by POE# descending; blank POE# rows go last
const poeNum = (it) => {
    const n = parseInt(it.poe_sort, 10);
    return isNaN(n) ? -1 : n;
};
export const sortItemsDesc = (items) => [...items].sort((a, b) => poeNum(b) - poeNum(a));

export const UnitTab = ({ unit, template, grades, unitMeta, locked, saveGrade, saveUnitMeta, openSig }) => (
    <div className="space-y-5">
        <Card>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
                {unit.evidence_reference && <span className="text-xs font-mono px-2 py-0.5 rounded bg-ica-navy/10 text-ica-navy dark:text-ica-light-gold">{unit.evidence_reference}</span>}
                <h2 className="text-base font-bold text-ica-navy dark:text-ica-light-gold">{unit.unit_no}: {unit.unit_name}</h2>
            </div>
            {unit.pm_refs && <p className="text-xs text-gray-500 dark:text-slate-400">{unit.pm_refs}</p>}
            {(unit.learning_units || []).map((lu, i) => <p key={i} className="text-xs text-gray-500 dark:text-slate-400">{lu}</p>)}
            <div className="mt-3 flex flex-wrap gap-2">
                {template.scale.map(s => (
                    <span key={s.score} className="text-[11px] px-2 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300"><b>{s.score}</b> {s.label}</span>
                ))}
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">Methods: {template.methods.map(m => `${m.name} (${m.abbr})`).join(', ')}</p>
        </Card>

        {unit.competence_sections.map((section, si) => (
            <Card key={si}>
                <h3 className="text-sm font-semibold text-ica-navy dark:text-ica-light-gold mb-3">Assessment of Practical Competence: {section.title}</h3>
                <div className="overflow-x-auto doc-scroll">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-ica-navy text-white text-xs">
                                <th className="px-2 py-2 text-left w-14">POE #</th>
                                <th className="px-3 py-2 text-left">{section.item_label}</th>
                                <th className="px-3 py-2 text-left">Preparation methods</th>
                                <th className="px-3 py-2 text-left">Cooking methods</th>
                                <th className="px-3 py-2 text-center">Assessment Score</th>
                                <th className="px-3 py-2 text-center w-36">Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {section.categories.map((cat, ci) => (
                                <React.Fragment key={ci}>
                                    <tr className="bg-gray-50 dark:bg-slate-700/40">
                                        <td colSpan={6} className="px-3 py-1.5 font-semibold text-gray-700 dark:text-slate-200 text-xs">{cat.name}</td>
                                    </tr>
                                    {sortItemsDesc(cat.items).map(it => {
                                        const g = grades[it.outcome_id] || {};
                                        return (
                                            <tr key={it.outcome_id} className="border-b border-gray-100 dark:border-slate-700" data-testid={`poe-outcome-row-${it.outcome_id}`}>
                                                <td className="px-2 py-2 text-xs text-gray-500 dark:text-slate-400 font-mono">{it.poe_sort ? `#${it.poe_sort}` : ''}</td>
                                                <td className="px-3 py-2 text-gray-800 dark:text-slate-200">{it.dish}</td>
                                                <td className="px-3 py-2 text-xs text-gray-500 dark:text-slate-400">{it.prep}</td>
                                                <td className="px-3 py-2 text-xs text-gray-500 dark:text-slate-400">{it.cooking}</td>
                                                <td className="px-3 py-2">
                                                    <ScoreSelector value={g.score ?? null} disabled={locked} onChange={(s) => saveGrade(it.outcome_id, s, g.date)} />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <Input type="date" value={g.date || ''} disabled={locked} className="h-8 text-xs"
                                                        onChange={(e) => saveGrade(it.outcome_id, g.score ?? null, e.target.value)} data-testid={`poe-date-${it.outcome_id}`} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        ))}

        {/* Unit signatures + comments */}
        <Card>
            <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                    <Label className="text-xs">Assessor Name</Label>
                    <Input value={unitMeta.assessor_name || ''} disabled={locked} onChange={(e) => saveUnitMeta(unit.id, { assessor_name: e.target.value })} data-testid="poe-unit-assessor-name" />
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">Date of Completion</Label>
                    <Input type="date" value={unitMeta.date_of_completion || ''} disabled={locked} onChange={(e) => saveUnitMeta(unit.id, { date_of_completion: e.target.value })} />
                </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <SignatureField label="Assessor Signature" value={unitMeta.assessor_signature} disabled={locked} onClick={() => openSig('Assessor Signature', (d) => saveUnitMeta(unit.id, { assessor_signature: d }))} />
                <SignatureField label="Student Signature" value={unitMeta.student_signature} disabled={locked} onClick={() => openSig('Student Signature', (d) => saveUnitMeta(unit.id, { student_signature: d }))} />
            </div>
            <div className="space-y-1 mt-4">
                <Label className="text-xs">Assessor Summarizing Comments</Label>
                <Textarea value={unitMeta.assessor_comments || ''} disabled={locked} rows={3} onChange={(e) => saveUnitMeta(unit.id, { assessor_comments: e.target.value })} data-testid="poe-unit-comments" />
            </div>
            {(unit.criteria || []).length > 0 && (
                <div className="mt-4">
                    <p className="text-xs font-semibold text-gray-700 dark:text-slate-200 uppercase tracking-wide mb-1">Assessment Criteria</p>
                    <ul className="list-disc list-inside text-xs text-gray-500 dark:text-slate-400 space-y-0.5">
                        {unit.criteria.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                </div>
            )}

            {/* IV / IQA */}
            <div className="mt-4 p-4 bg-gray-50 dark:bg-slate-700/40 rounded-lg">
                <p className="text-xs font-semibold text-gray-700 dark:text-slate-200 uppercase tracking-wide mb-3">Internal Verification & Quality Assurance (If Sampled)</p>
                <div className="grid sm:grid-cols-3 gap-3">
                    <div className="space-y-1">
                        <Label className="text-xs">Internal Verifier Name</Label>
                        <Input value={unitMeta.iv_name || ''} disabled={locked} onChange={(e) => saveUnitMeta(unit.id, { iv_name: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">IQA Date</Label>
                        <Input type="date" value={unitMeta.iqa_date || ''} disabled={locked} onChange={(e) => saveUnitMeta(unit.id, { iqa_date: e.target.value })} />
                    </div>
                    <SignatureField label="IV Signature" value={unitMeta.iv_signature} disabled={locked} onClick={() => openSig('IV Signature', (d) => saveUnitMeta(unit.id, { iv_signature: d }))} />
                </div>
                <div className="space-y-1 mt-3">
                    <Label className="text-xs">Internal Verifier Comments</Label>
                    <Textarea value={unitMeta.iv_comments || ''} disabled={locked} rows={2} onChange={(e) => saveUnitMeta(unit.id, { iv_comments: e.target.value })} />
                </div>
            </div>
        </Card>
    </div>
);
